import React, { useState } from "react";
import Box from "@mui/material/Box";
import GenieText from "../../../../components/core/genie-text/GenieText";
import { TextField, useMediaQuery } from "@mui/material";
import Button from "@mui/material/Button";
import { Link as ReactLink, useParams } from "react-router-dom";
import NumberFormat from "react-number-format";
import useCurrentCart from "../../../../queries/routes/useCurrentCart";
import { lineItemTotal } from "../../../../queries/utils/calculate";
import useUser from "../../../../queries/routes/useUser";
import { formatCurrency } from "../../../../utils/currency";
import usePutAddress from "../../../../queries/routes/usePutAddress";
import Addresses from "./Addresses";
import { theme } from "../../../../components/special/genie-theme/genie-theme";
import GenieSkeleton from "../../../../components/core/genie-skeleton/GenieSkeleton";
import useGetAddress from "../../../../queries/routes/useGetAddress";
import useAddAddressToOrder from "../../../../queries/routes/useAddAddressToOrder";
import { User } from "../../../../types/api/User";

const defaultValues: {
  firstName: string;
  lastName: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
} = {
  firstName: "",
  lastName: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  state: "",
  zip: "",
};

function Shipping() {
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const { org_id } = useParams();
  const [form, setForm] = useState<typeof defaultValues>(defaultValues);

  const { data: user } = useUser();
  const { data: cart } = useCurrentCart(org_id!, user?.organizations[0].id!);
  const { mutate: addAddress } = usePutAddress(user?.organizations[0].id!);
  const { data: addresses } = useGetAddress(user?.organizations[0].id!);

  const { mutate: addAddressToOrder } = useAddAddressToOrder(
    cart?.id,
    org_id!,
    user?.organizations[0].id!
  );

  const [isAddingAddress, setIsAddingAddress] = useState(false);
  const total = lineItemTotal(cart);

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = event.target;
    setForm({
      ...form,
      [name]: value,
    });
  };

  const submitAddress = () => {
    addAddress(
      {
        organization: {
          id: user?.organizations[0].id || "",
        },
        address: {
          city: form.city,
          country: "US",
          postal_code: form.zip,
          line1: form.addressLine1,
          line2: form.addressLine2,
          state: form.state,
        },
        is_default_shipping: true,
        is_default_billing: false,
      },
      {
        onSuccess: (user: User) => {
          setForm(defaultValues);
          addAddressToOrder({
            orderId: cart!.order!.id,
            addressId: user.address.id!,
          });
        },
      }
    );
  };
  return (
    <Box mt={2} mb={2}>
      <Box
        display={"flex"}
        alignItems={"center"}
        justifyContent={"space-between"}
      >
        <GenieText fontWeight={"bold"} fontSize={"small"}>
          Shipping Address
        </GenieText>
        <Button
          size={"small"}
          disabled={!cart?.order || !cart.order.shipping_address}
          component={ReactLink}
          variant={"outlined"}
          to={`/storefront/${org_id}/billing`}
        >
          Continue to shipping options
        </Button>
      </Box>

      <Addresses cart={cart} org_id={org_id} user={user} />

      <Box display={"flex"} alignItems={"flex-start"}>
        {(addresses && addresses.length === 0) || isAddingAddress ? (
          <Box
            component={"form"}
            mt={2}
            width={isMobile ? "100%" : `${(2 / 3) * 100}%`}
            display="grid"
            gridTemplateColumns="repeat(12, 1fr)"
            gap={2}
          >
            <Box gridColumn="span 6">
              <TextField
                id="firstName"
                label="First Name"
                variant="outlined"
                required
                fullWidth
                autoFocus
                name="firstName"
                value={form.firstName}
                onChange={handleInputChange}
              />
            </Box>
            <Box gridColumn="span 6">
              <TextField
                id="lastName"
                label="Last Name"
                variant="outlined"
                required
                fullWidth
                name="lastName"
                value={form.lastName}
                onChange={handleInputChange}
              />
            </Box>
            <Box gridColumn="span 12">
              <TextField
                id="addressLine1"
                label="Address"
                variant="outlined"
                required
                fullWidth
                name="addressLine1"
                value={form.addressLine1}
                onChange={handleInputChange}
              />
            </Box>
            <Box gridColumn="span 12">
              <TextField
                id="addressLine2"
                label="Apt / Floor / Suite (optional)"
                variant="outlined"
                fullWidth
                name="addressLine2"
                value={form.addressLine2}
                onChange={handleInputChange}
              />
            </Box>
            <Box gridColumn={"span 4"}>
              <TextField
                id="city"
                label="City"
                variant="outlined"
                fullWidth
                name="city"
                value={form.city}
                required
                onChange={handleInputChange}
              />
            </Box>
            <Box gridColumn={"span 4"}>
              <TextField
                id="state"
                label="State"
                variant="outlined"
                fullWidth
                name="state"
                value={form.state}
                required
                onChange={handleInputChange}
              />
            </Box>
            <Box gridColumn={"span 4"}>
              <NumberFormat
                customInput={TextField}
                id="zip"
                label="Zip"
                variant="outlined"
                fullWidth
                name="zip"
                value={form.zip}
                format="#####"
                mask="_"
                required
                onChange={handleInputChange}
              />
            </Box>
            <Box gridColumn={isMobile ? "span 12" : "span 4"}>
              <Button
                fullWidth
                variant={"outlined"}
                disabled={
                  form.addressLine1 === "" ||
                  form.zip === "" ||
                  form.city === "" ||
                  form.firstName === "" ||
                  form.lastName === ""
                }
                onClick={submitAddress}
              >
                Save Address
              </Button>
            </Box>

            <Box
              gridColumn={"span 12"}
              display={"flex"}
              mt={2}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <GenieText
                link={`/storefront/${org_id}`}
                style={{ cursor: "pointer" }}
                fontSize={"tiny"}
                underline
              >
                Back to store
              </GenieText>
              <Button
                disabled={!cart?.order || !cart.order.shipping_address}
                component={ReactLink}
                variant={"outlined"}
                to={`/storefront/${org_id}/billing`}
              >
                Continue to shipping options
              </Button>
            </Box>
          </Box>
        ) : (
          <Box width={`${(2 / 3) * 100}%`}>
            <Button onClick={() => setIsAddingAddress(true)}>
              Add New Address
            </Button>
          </Box>
        )}

        {!isMobile && (
          <Box width={`${(1 / 3) * 100}%`} pl={2}>
            <Box
              mb={1}
              display={"flex"}
              justifyContent={"space-between"}
              alignItems={"center"}
            >
              <GenieText fontSize={"small"}>Subtotal</GenieText>
              <GenieText fontSize={"small"}>
                {!cart ? (
                  <GenieSkeleton width={"100%"} height={24} />
                ) : (
                  formatCurrency(cart.total_cents)
                )}
              </GenieText>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
}

export default Shipping;
